import { computed, type Ref } from 'vue';
import { max } from 'lodash-es';
import dayjs from 'dayjs';

import { ARRIVAL_FIELD } from '@entities/dates-picker';
import { useFlexibleDates } from '@entities/flexible-dates';
import type { DateLib } from '@shared/lib';

import { useDatepickerState } from './use-datepicker-state';
import { useCalendarQuery } from './use-calendar-query';
import { useNightsQuery } from './use-nights-query';

const dateFormat = 'YYYY-MM-DD';
const defaultNumOfMonths = 12;

const addDays = (date: DateLib.DateProp, days: number) => {
  return dayjs(date).add(days, 'day').format(dateFormat);
};

export const useDatepicker = (
  countryToId: Ref<number>,
  townFromId: Ref<number>,
) => {
  const datesPicker = useDatepickerState();
  const flexibleDates = useFlexibleDates(
    datesPicker.depDate,
    datesPicker.arrDate,
  );

  const calendarQuery = useCalendarQuery(countryToId, townFromId);
  const nightsQuery = useNightsQuery({
    countryToId,
    townFromId,
    dateTo: flexibleDates.depToDate,
    dateFrom: flexibleDates.depFromDate,
  });

  const isDatepickerLoading = computed(
    () => calendarQuery.isFetching.value || nightsQuery.isFetching.value,
  );

  const availableDates = computed(() => calendarQuery.data.value ?? []);
  const availableNightsAmount = computed(() => nightsQuery.data.value ?? []);

  const availableArrivalDates = computed(() => {
    const departureDate = datesPicker.depDate.value as string;

    return availableNightsAmount.value.map(nightsAmount =>
      dayjs(departureDate).add(nightsAmount, 'day').format('YYYY-MM-DD'),
    );
  });

  const startDate = computed(() => availableDates.value[0]);
  const selectedDates = computed(() =>
    [datesPicker.depDate.value, datesPicker.arrDate.value].filter(Boolean),
  );

  const minDate = computed(() => {
    const dayJsAvailableDates = availableDates.value.map(date => dayjs(date));
    // dayjs.min returns null if an array is empty, but types seem not to cover that case
    const minDate = dayjs.min(dayJsAvailableDates) || dayjs();
    return minDate.format(dateFormat);
  });

  const maxDate = computed(() => {
    const lastAvailableDate =
      availableDates.value[availableDates.value.length - 1];
    const maxNightsCount = max(availableNightsAmount.value);

    if (lastAvailableDate && maxNightsCount) {
      return addDays(lastAvailableDate, maxNightsCount);
    }

    return '';
  });

  const numberOfMonth = computed(() => {
    const maxNightsCount = max(availableNightsAmount.value);

    if (maxNightsCount) {
      const maxAvailableDate = dayjs(flexibleDates.depToDate.value).add(
        Number(maxNightsCount),
        'day',
      );
      const monthCount = Math.ceil(
        dayjs(maxAvailableDate).diff(minDate.value, 'month', true),
      );

      return monthCount < defaultNumOfMonths ? defaultNumOfMonths : monthCount;
    }

    const selectedDateMonthsDiff =
      Math.ceil(
        dayjs(flexibleDates.depToDate.value).diff(minDate.value, 'month', true),
      ) + 1;

    return selectedDateMonthsDiff > defaultNumOfMonths
      ? selectedDateMonthsDiff
      : defaultNumOfMonths;
  });

  const isCountOfNightsUnavailable = computed(() => {
    const hasValues = datesPicker.arrDate.value && datesPicker.depDate.value;
    const nightsDifference = hasValues
      ? dayjs(datesPicker.arrDate.value).diff(datesPicker.depDate.value, 'day')
      : null;

    if (nightsDifference) {
      return !availableNightsAmount.value.includes(nightsDifference);
    }

    return false;
  });

  /**
   * This is a wrapper for quick implementation of the clear arrival date if
   * it is out of allowed arrival dates logic
   *
   * isCountOfNightsUnavailable becomes True when the interval between
   * departure and arrival dates is not allowed
   *
   * So we need to clear arrival date
   */
  const handleDateChangeAndClearArrival = (dates: string[]) => {
    datesPicker.handleDateChange(dates);

    if (isCountOfNightsUnavailable.value) {
      datesPicker.clearDateField(ARRIVAL_FIELD);
    }
  };

  return {
    isDatepickerLoading,
    flexibleDates,
    isCountOfNightsUnavailable,
    datesPicker: {
      ...datesPicker,
      handleDateChange: handleDateChangeAndClearArrival,
      startDate,
      availableDates,
      availableArrivalDates,
      numberOfMonth,
      selectedDates,
      minDate,
      maxDate,
    },
  };
};
